import { useCallback, useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import {
  fetchConditionsAndSlotsAsync,
  selectAppState,
  setActivePage,
  setIFrameLink,
  setShowFlowBuilder,
  updateBreadcrumbValue,
} from "../app/slice/appSlice";
import {
  addVariant,
  selectCondition,
  updateVariantStatus,
  selectConditionAsync,
  setSelectedSlot,
  setSelectedVariantId,
} from "../app/slice/conditionSlice";
import { addSlot, removeSlot, updateSlot } from "../app/slice/slotSlice";
import { openMissingEntitiesModal } from "../app/slice/missingEntitiesSlice";
import { APP_PLATFORM, WIDGET_EDITOR_URL } from "../config";
import { showCrispChat, sendSupportMessage } from "../utils/crisp";
import { Platform, Slot } from "../models";
import {
  AIChatbotPostMessageEventType,
  generateBreadcrumbFormat,
  IFramePostMessageType,
  PENDING_WIDGET_ACTIONS_STORAGE_KEY,
  sendPostMessage,
} from "../utils/helpers";
import { PageType } from "@reconvert/reconvert-utils";
import { RootState, store } from "../app/store";
import { FunctionRegistry } from "../utils/functionRegistry";
import { getToken } from "../services/auth.service";
import { getVariantById } from "../services/variants.service";
import i18n from "../utils/i18n";
import useUnsavedBar from "./useUnsavedBar";
import type {
  PendingFlowState,
  ToolOutputsMessageEvent,
  UseAIChatContextReturn,
} from "../types/aiChatBot";

/**
 * Centralized all AI Chatbot ↔ Flow Builder communication.
 *
 * Responsibilities:
 * 1. Sends context updates (current view, selected page) to the AI Chatbot via postMessage
 *    so it always knows the user's current position in the Flow Builder.
 * 2. Handles tool-output actions received from the AI Chatbot (via the parent iframe)
 *    such as navigating to a page/condition/variant, opening the widget editor,
 *    and mutating slots. All actions are gated by the unsaved-changes modal — if the
 *    user has pending edits, they must save or discard before the action proceeds.
 * 3. Manages flow-state batching (pendingFlowStateRef + scheduleProcessFlowState) so
 *    rapid successive updates (page → condition → variant) are coalesced into a single
 *    Redux dispatch cycle.
 *
 * The hook exposes its handler via a ref (processToolOutputsActionsRef) so the
 * existing message listener in useAppInit can call it without adding a function
 * to the useEffect dependency array.
 */
const useAIChatContext = (): UseAIChatContextReturn => {
  const { activePage, showFlowBuilder } = useAppSelector(selectAppState);
  const dispatch = useAppDispatch();
  const { preventAction, unsavedChanges } = useUnsavedBar();

  const pendingFlowStateRef = useRef<PendingFlowState>({});
  const skipLoadConditionsRef = useRef(false);
  const processFlowStateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const preventActionRef = useRef(preventAction);

  preventActionRef.current = preventAction;

  // --- Context sync: tell the AI Chatbot which view/page the user is on ---

  useEffect(() => {
    if (APP_PLATFORM === Platform.Shopify) {
      sendPostMessage(IFramePostMessageType.UPDATE_AI_CHAT_CONTEXT, {
        currentView: 'FLOW_BUILDER',
        eventType: AIChatbotPostMessageEventType.UPDATE_CURRENT_VIEW,
      });
    }
  }, []);

  useEffect(() => {
    if (APP_PLATFORM === Platform.Shopify && showFlowBuilder) {
      sendPostMessage(IFramePostMessageType.UPDATE_AI_CHAT_CONTEXT, { page: activePage, eventType: AIChatbotPostMessageEventType.SET_SELECTED_PAGE });
    } else if (APP_PLATFORM === Platform.Shopify && !showFlowBuilder) {
      sendPostMessage(IFramePostMessageType.UPDATE_AI_CHAT_CONTEXT, { page: undefined, eventType: AIChatbotPostMessageEventType.SET_SELECTED_PAGE });
    }
  }, [showFlowBuilder, activePage]);


  const hasUnsavedChanges = unsavedChanges.length > 0;

  useEffect(() => {
    if (APP_PLATFORM === Platform.Shopify) {
      sendPostMessage(IFramePostMessageType.UPDATE_AI_CHAT_CONTEXT, {
        hasUnsavedChanges,
        eventType: AIChatbotPostMessageEventType.UPDATE_HAS_UNSAVED_CHANGES,
      });
    }
  }, [hasUnsavedChanges]);

  // --- Flow-state processing: applies batched page/condition/variant/slot changes ---

  /**
   * Reads the accumulated pending state, resets it, then sequentially applies
   * page → condition → slot → variant changes. While running, skipLoadConditionsRef
   * is true so useLoadConditionsOnActivePageChange won't refetch and overwrite.
   */
  const runProcessFlowState = useCallback(async () => {
    const pending = { ...pendingFlowStateRef.current };

    pendingFlowStateRef.current = {};
    const { updatedPage, updatedConditionId, updatedVariantId, updatedSlotId } = pending;

    if (!updatedPage && !updatedConditionId && !updatedVariantId) return;

    skipLoadConditionsRef.current = true;
    try {
      const currentState = store.getState() as RootState;

      if (updatedPage) {
        dispatch(setShowFlowBuilder(true));
        FunctionRegistry.removeQueryParamsFromUrl?.();
      }

      if (updatedPage && updatedPage !== currentState.app.activePage) {
        dispatch(setActivePage(updatedPage));
        await dispatch(fetchConditionsAndSlotsAsync()).unwrap();
      }

      const currentConditionId = currentState.conditions.selectedConditionId;

      if (updatedConditionId && currentConditionId !== updatedConditionId) {
        dispatch(selectCondition(updatedConditionId));
        const res = await dispatch(selectConditionAsync(updatedConditionId)).unwrap();

        if (res?.data) {
          dispatch(
            updateBreadcrumbValue({
              breadCrumb: generateBreadcrumbFormat({ page: res?.data?.location, condition: res?.data?.name }),
            }),
          );
        }
      }

      if (updatedSlotId) {
        const updatedSlot = currentState.slots.slots.find((slot) => slot.id === updatedSlotId);

        if (updatedSlot) {
          dispatch(setSelectedSlot(updatedSlot));
        }
      }

      if (updatedVariantId) {
        dispatch(setSelectedVariantId(updatedVariantId));
      }
    } finally {
      skipLoadConditionsRef.current = false;
    }
  }, [dispatch]);

  /** Debounces runProcessFlowState so rapid successive updates are coalesced. */
  const scheduleProcessFlowState = useCallback(() => {
    if (processFlowStateTimeoutRef.current) clearTimeout(processFlowStateTimeoutRef.current);

    processFlowStateTimeoutRef.current = setTimeout(() => {
      processFlowStateTimeoutRef.current = null;
      runProcessFlowState();
    }, 0);
  }, [runProcessFlowState]);

  useEffect(() => {
    return () => {
      if (processFlowStateTimeoutRef.current) clearTimeout(processFlowStateTimeoutRef.current);
    };
  }, []);

  // --- Tool-output action handlers (invoked by the AI Chatbot via postMessage) ---

  /**
   * Handles individual tool-output actions sent by the AI Chatbot:
   *  - update-flow-state: navigate to a specific page/condition/variant/slot
   *  - open-widget-editor: open the unified editor for a given variant
   *  - update-slot-name:   rename one or more slots
   *  - add-slot:           create slots (and fetch their variants)
   *  - delete-slot:        remove slots by slotNo
   */
  const executeToolOutputsActions = useCallback(async (event: ToolOutputsMessageEvent) => {
    const { data: eventData } = event;

    if (eventData.action === 'update-flow-state') {
      pendingFlowStateRef.current = {
        updatedPage: eventData.data.page as PageType | undefined,
        updatedConditionId: eventData.data.conditionId,
        updatedVariantId: eventData.data.variantId,
        updatedSlotId: eventData.data.slotId,
      };
      scheduleProcessFlowState();
      return
    }

    if (eventData.action === 'open-widget-editor') {
      const { variantId, conditionId, slotId } = eventData.data;

      if (eventData.pendingWidgetActions?.length) {
        try {
          sessionStorage.setItem(PENDING_WIDGET_ACTIONS_STORAGE_KEY, JSON.stringify(eventData.pendingWidgetActions));
        } catch (error) {
          console.error("Error setting pending widget actions:", error);
        }
      }

      try {
        const currentState = store.getState() as RootState;
        const platformStoreId = currentState.app.platformStoreId;
        const activePage = currentState.app.activePage;

        const tokenRes = await getToken(platformStoreId!);
        let link = `${WIDGET_EDITOR_URL}?variant=${variantId}${activePage! ? `&type=${activePage!}` : ""
          }&platformStoreId=${platformStoreId}&token=${tokenRes.response.token}&conditionId=${conditionId}&slotId=${slotId}&language=${i18n.language}`;

        if (tokenRes.response?.token) {
          dispatch(setIFrameLink(link));
        }
      } catch (error) { }
      return;
    }

    if (eventData.action === 'update-slot-name') {
      const actionsList = eventData.data.actions ?? [];

      for (const { slotId, slotName } of actionsList) {
        dispatch(updateSlot({ id: slotId, name: slotName }));
      }

      return;
    }

    if (eventData.action === 'add-slot') {
      const actionsList = eventData.data.actions ?? [];
      const currentState = store.getState() as RootState;
      const activePage = currentState.app.activePage;
      const platformStoreId = currentState.app.platformStoreId;

      for (const data of actionsList) {
        const slot = data.slot;
        const id = slot?.id ?? slot?._id;
        const slotNo = slot?.slotNo ?? 0;

        if (id != null) {
          dispatch(
            addSlot({
              slot: { ...slot, id, _id: id, slotNo } as Partial<Slot> & { id: string; slotNo: number },
              activePage: (data.page as PageType) ?? activePage,
              platformStoreId: platformStoreId ?? "",
            }),
          );
        }

        const variants = Array.isArray(slot?.variants) ? slot.variants : [];

        for (const variant of variants) {
          const variantId = variant?.id ?? (variant as { _id?: string })?._id;

          if (variantId) {
            try {
              const fetchedVariant = await getVariantById(variantId);

              dispatch(
                addVariant({
                  conditionId: data.conditionId,
                  variant: { ...fetchedVariant, id: fetchedVariant.id ?? variantId },
                }),
              );
            } catch {
              dispatch(
                addVariant({
                  conditionId: data.conditionId,
                  variant: { ...variant, id: variantId },
                }),
              );
            }
          }
        }
      }

      return;
    }

    if (eventData.action === 'delete-slot') {
      const actionsList = eventData.data.actions ?? [];
      const currentState = store.getState() as RootState;
      const activePage = currentState.app.activePage;
      const platformStoreId = currentState.app.platformStoreId;

      for (const data of actionsList) {
        dispatch(
          removeSlot({
            slotNo: data.slotNo,
            activePage: (data.page as PageType) ?? activePage,
            platformStoreId: platformStoreId ?? "",
          }),
        );
      }

      return;
    }

    if (eventData.action === 'update-slot-status') {
      const actionsList = eventData.data.actions ?? [];

      for (const data of actionsList) {
        if (data) {
          dispatch(updateVariantStatus({ active: data.active, slotId: data.slotId, conditionId: data.conditionId }));
        }
      }
    }

    if (eventData.action === 'missing-entities') {
      const missingEntities = Array.isArray(eventData.data.missingEntities)
        ? eventData.data.missingEntities
        : [];

      const needsProductOrCollection = missingEntities.some(
        (e) => e === 'product_id' || e === 'product_ids' || e === 'collection_id'
      );

      if (needsProductOrCollection) {
        dispatch(openMissingEntitiesModal({ missingEntities }));
      }
    }

    if (eventData.action === 'open-support-chat') {
      const title = eventData.data.title ?? "Support request";
      const description = eventData.data.description ?? "";

      showCrispChat();
      sendSupportMessage(title, description);
    }
  }, [dispatch, scheduleProcessFlowState]);

  const executeToolOutputsActionsRef = useRef(executeToolOutputsActions);

  executeToolOutputsActionsRef.current = executeToolOutputsActions;

  // --- Public handler: gates tool-output actions behind the unsaved-changes modal ---

  /**
   * Wraps executeToolOutputsActions with preventAction so that if the user has
   * unsaved changes, the save/discard modal is shown first. The actual action
   * only runs after the user resolves the modal (or immediately if nothing is dirty).
   * Exposed via a ref so useAppInit's message listener can call it without
   * adding a function dependency to its useEffect.
   */
  const processToolOutputsActions = useCallback(async (event: ToolOutputsMessageEvent) => {
    preventActionRef.current(() => executeToolOutputsActionsRef.current(event));
  }, []);

  const processToolOutputsActionsRef = useRef(processToolOutputsActions);

  return {
    skipLoadConditionsRef,
    processToolOutputsActionsRef,
  };
};

export default useAIChatContext;
